/**
 * Lớp nối giữa response của service và bảng hai báo cáo M18 (SCR-M18-001 · SCR-M18-005).
 *
 * ⚠️ **Nằm ở `helpers/` là có chủ đích, không phải cho gọn.** Repo webapp không có
 *    react-testing-library nên KHÔNG file test nào import một component nào — mọi logic để trong
 *    `.js` của màn hình đều có mutation score 0 theo cấu tạo. Vòng review tìm được bốn lỗi CHẶN
 *    và cả bốn nằm đúng trong lớp nối này: quên mang `efficiency` sang dòng cha, đọc ngược vai
 *    `type`, gọi sai chữ ký `setFilter`, truyền object bộ lọc vào một helper nhận `moment`. Không
 *    cái nào làm vỡ màn hình — chúng chỉ lặng lẽ xoá số đi.
 */

/** Dòng CHA mang `type` này ở CẢ HAI màn; dòng CON mang `'stage'`. Xem `rowsOf`. */
export const PARENT_ROW = 'actor'
export const CHILD_ROW = 'stage'

/**
 * Chuẩn hoá hai hình dạng response về MỘT danh sách nhóm, để phần dựng dòng và các cột không phải
 * biết đang gộp theo trục nào.
 *
 * `children` là mảng (tài khoản × công đoạn) GIỐNG HỆT nhau ở cả hai đường — chỉ nhãn dòng cha và
 * đích khoan sâu là khác.
 *
 * ⚠️ `efficiency` PHẢI đi theo. Các cột hiệu suất đọc nó từ dòng cha; đánh rơi ở đây là cả năm cột
 *    hiện `—` và cột Xếp hạng hiện "Không áp dụng" cho mọi người, kể cả người service đã chấm
 *    hạng A. Service vẫn trả đủ số nên không lỗi nào báo.
 */
export const groupsOf = (report, byStage) => {
    if (!report) return []

    if (byStage) {
        return (report.stages || []).map((entry) => ({
            key: entry.stageCode,
            actorLabel: `${(entry.actors || []).length} tài khoản`,
            stageLabel: entry.name,
            summary: entry.summary,
            efficiency: entry.efficiency,
            children: entry.actors || [],
            // Dòng cha ở màn này là MỘT công đoạn, mọi tài khoản -> khoan sâu không kèm tài khoản.
            drillActor: undefined,
            drillStageCode: entry.stageCode,
        }))
    }

    return (report.actors || []).map((entry) => ({
        key: entry.actor,
        actorLabel: entry.actor,
        stageLabel: `${(entry.stages || []).length} công đoạn`,
        summary: entry.summary,
        efficiency: entry.efficiency,
        children: entry.stages || [],
        drillActor: entry.actor,
        drillStageCode: undefined,
    }))
}

/**
 * Dàn phẳng thành ba loại dòng: dòng cha, dòng con, và dòng cảnh báo gộp cột. Dòng cảnh báo chỉ
 * hiện khi dòng cha đang mở rộng.
 *
 * ⚠️ `type` nói về HÌNH DẠNG DỮ LIỆU (`row.group` hay `row.stage`), KHÔNG nói về vai trên màn
 *    hình. Dòng cha là `'actor'` kể cả ở màn theo công đoạn — ở đó nó là một công đoạn.
 */
export const rowsOf = (groups, expanded, dataCoverageThreshold = 0.9) => {
    const flat = []

    ;(groups || []).forEach((group) => {
        const isOpen = !!(expanded || {})[group.key]

        flat.push({
            key: group.key,
            type: PARENT_ROW,
            group,
            isOpen,
            drillActor: group.drillActor,
            drillStageCode: group.drillStageCode,
        })

        if (!isOpen) return

        group.children.forEach((child) => {
            flat.push({
                key: `${group.key}-${child.actor}-${child.stageCode}`,
                type: CHILD_ROW,
                actor: child.actor,
                stage: child,
                // Dòng con LUÔN xác định cả hai trục, dù đang gộp theo trục nào.
                drillActor: child.actor,
                drillStageCode: child.stageCode,
            })
        })

        const low = (value) => value !== null && value !== undefined && value < dataCoverageThreshold
        const lowStandard = low(group.summary.hasStandard)
        const lowFileData = low(group.summary.hasFileData)

        if (lowStandard || lowFileData) {
            flat.push({key: `${group.key}-warning`, type: 'warning', group, lowStandard, lowFileData})
        }
    })

    return flat
}

/**
 * Khối hiệu suất của một dòng, dù dòng đó là cha hay con, ở màn nào.
 *
 * Một bảng tra vai duy nhất, để mỗi cột không phải tự nhớ `type` nào ứng với `group` hay `stage`.
 * Trả `null` cho dòng cảnh báo và cho dòng chưa có số — KHÔNG trả object rỗng, vì cột phân biệt
 * "chưa đo được" với "đo ra 0" bằng chính chỗ `null` này.
 */
export const efficiencyOfRow = (row) => {
    if (!row) return null
    if (row.type === PARENT_ROW) return (row.group && row.group.efficiency) || null
    if (row.type === CHILD_ROW) return (row.stage && row.stage.efficiency) || null

    return null
}

/**
 * Số giờ cột "Giờ làm thực tế" phải hiện cho một dòng.
 *
 * ⚠️ Dòng CHA là một NGƯỜI, nên nó hiện giờ ĐÃ NHẬP (`actualHours`). Dòng CON là một ô
 *    tài khoản × công đoạn, ở đó giờ đã nhập không có nghĩa — chỉ có phần giờ PHÂN BỔ cho công
 *    đoạn đó (`allocatedHours`).
 *
 * ⚠️ Hai field này từng BẰNG NHAU ở mọi dòng, nên cột mang tên "Giờ làm thực tế" đọc
 *    `allocatedHours` mà không ai thấy sai. Chúng khác nhau đúng ở nhóm In/Cắt/Khắc: giờ có thật,
 *    nhưng `by_detect` chưa quy đổi được nên phân bổ bằng 0. Đọc nhầm field là hiện "0,0 h" cho
 *    người đã chấm công đủ cả tháng.
 *
 * `null` = chưa nhập giờ ngày nào; `0` = đã nhập nhưng không công đoạn nào phân bổ được. Trả
 * `undefined` cho dòng không có khối hiệu suất để chỗ gọi tự quyết.
 */
export const workHoursOfRow = (row, eff) => {
    if (!eff) return undefined
    if (row && row.type === PARENT_ROW) return eff.actualHours

    return eff.allocatedHours
}

/**
 * Có dòng nào đang MỞ không — quyết định hai cột metadata (`Đơn vị`, `Tính giờ`) hiện hay ẩn.
 *
 * ⚠️ `.some(Boolean)` chứ KHÔNG phải `Object.keys(...).length`: `setExpanded` ghi `false` khi người
 *    dùng ĐÓNG một dòng chứ không xoá khoá, nên đếm khoá là mở rồi đóng lại vẫn coi như đang mở —
 *    hai cột kia không bao giờ biến mất nữa và bảng vĩnh viễn rộng thêm 250px. Không lỗi nào báo.
 */
export const anyExpandedOf = (expanded) => Object.values(expanded || {}).some(Boolean)
